import { SITE_NAME, SITE_URL, DEFAULT_OG_IMAGE_PATH } from '../config/siteSeo';

const companyAddress = {
  '@type': 'PostalAddress',
  streetAddress: 'H.No. 135, Rajpuram, Chipiyana Buzurg',
  addressLocality: 'Gautam Buddha Nagar',
  addressRegion: 'UP',
  postalCode: '201009',
  addressCountry: 'IN',
};

const openingHours = [
  {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    opens: '09:00',
    closes: '18:00',
  },
];

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': ['Organization', 'LocalBusiness'],
  '@id': `${SITE_URL}/#organization`,
  name: 'Plux Infosystems Private Limited',
  alternateName: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/img/logo.png`,
  image: `${SITE_URL}${DEFAULT_OG_IMAGE_PATH}`,
  description:
    'Plux Infosystems is an India-based web development, ecommerce, AI automation, digital marketing, and product engineering company.',
  email: 'info@pluxinfosystems.com',
  priceRange: '$$',
  address: companyAddress,
  areaServed: ['IN', 'US', 'GB', 'AU', 'CA'],
  sameAs: [
    'https://www.facebook.com/pluxinfosystems',
    'https://twitter.com/chandra353',
    'https://www.linkedin.com/company/plux-infosystems-private-limited',
    'https://business.google.com/website/pluxinfosystems',
    'https://github.com/kailash-programmer',
  ],
  openingHoursSpecification: openingHours,
  contactPoint: [
    {
      '@type': 'ContactPoint',
      email: 'info@pluxinfosystems.com',
      contactType: 'customer service',
      areaServed: 'IN',
      availableLanguage: ['English', 'Hindi'],
      contactOption: 'Email',
    },
  ],
};

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: `${SITE_URL}/`,
  publisher: {
    '@id': `${SITE_URL}/#organization`,
  },
};

export const professionalServiceSchema = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  '@id': `${SITE_URL}/#professional-service`,
  name: SITE_NAME,
  url: `${SITE_URL}/`,
  image: `${SITE_URL}/img/logo.png`,
  email: 'info@pluxinfosystems.com',
  priceRange: '$$',
  areaServed: 'Worldwide',
  parentOrganization: {
    '@id': `${SITE_URL}/#organization`,
  },
  address: companyAddress,
  openingHoursSpecification: openingHours,
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 28.6147,
    longitude: 77.37516,
  },
  serviceType: [
    'Web development',
    'Product engineering',
    'Ecommerce development',
    'Magento development',
    'AI automation',
    'WordPress development',
    'Website maintenance',
  ],
};

/** Sitewide JSON-LD graph injected on every route. */
export const sitewideSchemas = [websiteSchema, organizationSchema, professionalServiceSchema];

export function buildWebPageSchema({
  path,
  title,
  description,
}: {
  path: string;
  title: string;
  description: string;
}) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = `${SITE_URL}${normalizedPath}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: title,
    description,
    isPartOf: {
      '@id': `${SITE_URL}/#website`,
    },
    about: {
      '@id': `${SITE_URL}/#organization`,
    },
    publisher: {
      '@id': `${SITE_URL}/#organization`,
    },
  };
}

export function buildBlogPostingSchema({
  slug,
  title,
  description,
  date,
  dateModified,
  featuredImage,
  category,
}: {
  slug: string;
  title: string;
  description: string;
  date: string;
  dateModified?: string;
  featuredImage: string;
  category: string;
}) {
  const url = `${SITE_URL}/blogs/${slug}`;
  const imagePath = featuredImage.startsWith('/') ? featuredImage : `/${featuredImage}`;
  const imageUrl = /^https?:\/\//i.test(featuredImage) ? featuredImage : `${SITE_URL}${imagePath}`;
  const modified = dateModified && dateModified !== date ? dateModified : undefined;

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${url}#article`,
    headline: title,
    description,
    datePublished: date,
    ...(modified ? { dateModified: modified } : {}),
    image: imageUrl,
    articleSection: category,
    url,
    author: {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: SITE_NAME,
    },
    publisher: {
      '@id': `${SITE_URL}/#organization`,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    isPartOf: {
      '@id': `${SITE_URL}/#website`,
    },
  };
}

export function buildBreadcrumbSchema(items: Array<{ name: string; path: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.path.startsWith('/') ? item.path : `/${item.path}`}`,
    })),
  };
}
